import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { api } from '../api/client';
import type { Bill, BillLineItem, TOUConsumption } from '../api/types';
import BillAuditPanel from '../components/BillAuditPanel';
import { Card, Badge, Alert, DataTable } from '../components/ui';
import type { BadgeTone, DataTableColumn } from '../components/ui';
import { fmtCurrencyOrDash } from '../lib/fmt';
import { chargeTypeName } from '../lib/chargeType';

export default function BillDetail() {
  const { id } = useParams<{ id: string }>();
  const [bill, setBill] = useState<Bill | null>(null);
  const [lineItems, setLineItems] = useState<BillLineItem[]>([]);
  const [touConsumption, setTouConsumption] = useState<TOUConsumption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // PDF/Email state
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailAddress, setEmailAddress] = useState('');
  const [emailSuccess, setEmailSuccess] = useState<string | null>(null);

  useEffect(() => {
    if (id) {
      loadData();
    }
  }, [id]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [billRes, lineItemsRes, touRes] = await Promise.all([
        api.getBill(id!),
        api.getBillLineItems(id!),
        api.getBillTOUConsumption(id!),
      ]);

      setBill(billRes.data);
      setLineItems(lineItemsRes.data);
      setTouConsumption(touRes.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load bill');
    } finally {
      setLoading(false);
    }
  };

  const handleGeneratePdf = async () => {
    try {
      setGeneratingPdf(true);
      setError(null);
      await api.generateBillPdf(id!);
      await loadData(); // Refresh to get updated pdf_url
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate PDF');
    } finally {
      setGeneratingPdf(false);
    }
  };

  const handleViewPdf = async () => {
    try {
      const { url } = await api.getBillPdfUrl(id!);
      window.open(url, '_blank');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to get PDF URL');
    }
  };

  const handleDownloadPdf = async () => {
    try {
      const { downloadUrl } = await api.getBillPdfUrl(id!);
      window.open(downloadUrl, '_blank');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to get PDF URL');
    }
  };

  const handleSendEmail = async () => {
    try {
      setSendingEmail(true);
      setError(null);
      setEmailSuccess(null);
      await api.sendBillEmail(id!, emailAddress || undefined);
      setEmailSuccess('Email sent successfully');
      setShowEmailModal(false);
      setEmailAddress('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send email');
    } finally {
      setSendingEmail(false);
    }
  };

  // Converged onto the shared formatter (src/lib/fmt.ts), which is where the
  // non-breaking separator after "R" lives — the hand-rolled ASCII space was
  // the only break opportunity in the string and orphaned the "R" onto its own
  // line in narrow table columns.
  const formatCurrency = fmtCurrencyOrDash;

  // Rates show up to 4 decimals to preserve fractional cent precision.
  // Same shared formatter as the amounts above — see the non-breaking
  // separator note in src/lib/fmt.ts.
  const formatRate = (cents: number | null | undefined) => fmtCurrencyOrDash(cents, 4);

  // Format meter-side wall-clock timestamps (max_kva_timestamp etc.).
  // The ISO string we receive already encodes the DB wall-clock as-if-UTC —
  // the timestamp column is stored without TZ on the Portal side, and Hyperdrive's
  // EXTRACT(EPOCH ...) treats it as UTC. Rendering with `timeZone: 'UTC'` here
  // displays the same wall-clock the meter reported, matching what you see in
  // OR's data view, instead of double-shifting by the browser's local offset.
  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('en-ZA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'UTC',
    });
  };

  const formatNumber = (num: number | null | undefined, decimals = 2) => {
    if (num === null || num === undefined) return '-';
    return num.toLocaleString('en-ZA', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
  };

  // Same STATUS_TONE shape as BillRunDetail.tsx's shared bill/bill-run
  // status renderer (calculated/adjusted/approved/finalized subset).
  const STATUS_TONE: Record<string, BadgeTone> = {
    calculated: 'neutral',
    adjusted: 'orange',
    approved: 'success',
    finalized: 'purple',
  };
  const getStatusBadge = (status: string) => (
    <Badge tone={STATUS_TONE[status] ?? 'neutral'}>{status.replace('_', ' ')}</Badge>
  );

  // peak/standard/off_peak -> danger/warning/success, matching Tariffs.tsx's
  // TOU_TONE precedent (same red/yellow->amber/green->teal hue
  // normalization already established there).
  const TOU_TONE: Record<string, BadgeTone> = {
    peak: 'danger',
    standard: 'warning',
    off_peak: 'success',
  };
  const getTOUBadge = (bucket: string) => (
    <Badge tone={TOU_TONE[bucket] ?? 'neutral'}>{(bucket || 'all').replace('_', ' ')}</Badge>
  );

  // Line items are a genuinely variable-length list (up to ~18 distinct
  // charge_types × up to 2 seasons × up to 3 TOU buckets for a complex
  // Megaflex/Ruraflex tariff — not the small fixed handful the TOU
  // Consumption breakdown below is), so this one migrates to DataTable.
  // Description identifies the row and Amount is the number that matters
  // (the Rand value the line contributes to the bill); Type/Quantity/Rate
  // stay a tap away. Sorted into a copy so DataTable's data prop doesn't
  // mutate the lineItems state array in place.
  const sortedLineItems = [...lineItems].sort((a, b) => a.sort_order - b.sort_order);
  const lineItemColumns: DataTableColumn<BillLineItem>[] = [
    {
      key: 'description',
      label: 'Description',
      priority: 'primary',
      render: (item) => (
        <>
          <div className="font-medium text-content">{item.description}</div>
          {item.tou_bucket && item.tou_bucket !== 'all' && (
            <div className="mt-1">{getTOUBadge(item.tou_bucket)}</div>
          )}
        </>
      ),
    },
    {
      key: 'type',
      label: 'Type',
      priority: 'secondary',
      // Category name (src/lib/chargeType.ts) — no unit, no utility.
      // `charge_type` is both unit- and utility-asserting, and both lie on
      // water: the water line carries `energy_kwh` with `unit: 'kL'`, so the
      // raw key rendered "energy kwh" beside a "Water Charge" description, a
      // units-in-label map renders "Energy (kWh)" on a kL row, and even the
      // unit-free "Energy" still claims water is energy. This column sits
      // beside Fixed/Service/Network — it names the KIND of charge, so
      // `energy_kwh` reads "Consumption", which is true of both utilities.
      // The Quantity/Rate columns two over already print `item.unit`
      // ("13,91 kL", "R 25,91/kL").
      render: (item) => <span className="text-content-muted text-sm">{chargeTypeName(item.charge_type)}</span>,
    },
    {
      key: 'quantity',
      label: 'Quantity',
      priority: 'secondary',
      numeric: true,
      render: (item) => <span className="font-mono">{formatNumber(item.quantity)} {item.unit}</span>,
    },
    {
      key: 'rate',
      label: 'Rate',
      priority: 'secondary',
      numeric: true,
      render: (item) => <span className="font-mono">{formatRate(item.rate_cents)}{item.unit ? `/${item.unit}` : ''}</span>,
    },
    {
      key: 'amount',
      label: 'Amount',
      priority: 'primary',
      numeric: true,
      render: (item) => <span className="font-semibold">{formatCurrency(item.amount_cents)}</span>,
    },
  ];

  if (loading) {
    return (
      <div className="p-4 lg:p-8">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-border-token rounded w-64"></div>
          <div className="h-48 bg-border-token rounded-xl"></div>
          <div className="h-64 bg-border-token rounded-xl"></div>
        </div>
      </div>
    );
  }

  if (error || !bill) {
    return (
      <div className="p-4 lg:p-8">
        <Alert tone="danger" className="text-center">
          <p>{error || 'Bill not found'}</p>
          <Link
            to="/bill-runs"
            className="inline-block mt-4 px-4 py-2 bg-accent text-accent-fg rounded-lg hover:opacity-90 transition-colors"
          >
            Back to Bill Runs
          </Link>
        </Alert>
      </div>
    );
  }

  // Server-decided (billing-api attaches it via shared/billing-core). Never
  // re-derive from a unit string here — see the note on `Bill.utility` in
  // api/types.ts for why `unit === 'kWh'` is not a valid electricity test.
  const isWater = bill.utility === 'water';

  return (
    <div className="p-4 lg:p-8">
      {/* Breadcrumb */}
      <div className="mb-4">
        <Link to={`/bill-runs/${bill.bill_run_id}`} className="text-accent hover:opacity-90 text-sm">
          &larr; Back to Bill Run
        </Link>
      </div>

      {/* Header */}
      <div className="flex items-start justify-between mb-8">
        <div>
          {bill.tenant_name_snapshot && (
            <div className="mb-2 flex items-center gap-2">
              {/* Carries the tenant's own name (arbitrary data), not a fixed
                  enum label — doesn't fit Badge's chip shape, so this stays
                  hand-rolled with dark: added (same treatment, same computed
                  ratios, as the identical chip in BillRunDetail.tsx). */}
              <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 text-xs rounded-full ${
                bill.tenant_id ? 'bg-green-50 text-green-800 dark:bg-green-900 dark:text-green-100' : 'bg-amber-50 text-amber-800 dark:bg-amber-900 dark:text-amber-100'
              }`}>
                <span className={`w-1.5 h-1.5 rounded-full ${
                  bill.tenant_id ? 'bg-green-500' : 'bg-amber-500'
                }`} />
                {bill.tenant_name_snapshot}
              </span>
              {bill.segment_start && bill.segment_end && (
                <span className="text-xs text-content-muted">
                  {bill.segment_start} → {bill.segment_end}
                </span>
              )}
              {bill.tenant_account_number_snapshot && (
                <span className="text-xs text-content-muted font-mono">Acc: {bill.tenant_account_number_snapshot}</span>
              )}
            </div>
          )}
          <h1 className="text-2xl font-bold text-content">
            {bill.meter_name || bill.meter_asset_id}
          </h1>
          {bill.building_name && (
            <p className="text-content-muted mt-1">{bill.building_name}</p>
          )}
          <p className="text-sm text-content-muted mt-1">
            Tariff: {bill.tariff_name || 'Default'}
          </p>
        </div>
        <div className="text-right">
          <p className="text-3xl font-bold text-content">{formatCurrency(bill.total_amount_cents)}</p>
          <div className="mt-2">{getStatusBadge(bill.status)}</div>

          {/* PDF Actions */}
          <div className="mt-4 flex gap-2 justify-end">
            {bill.pdf_url ? (
              <>
                <button
                  onClick={handleViewPdf}
                  className="px-3 py-1.5 text-sm bg-surface-sunken text-content rounded-lg hover:bg-border-token transition-colors flex items-center gap-1.5"
                  title="View PDF"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                  View
                </button>
                <button
                  onClick={handleDownloadPdf}
                  className="px-3 py-1.5 text-sm bg-surface-sunken text-content rounded-lg hover:bg-border-token transition-colors flex items-center gap-1.5"
                  title="Download PDF"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  Download
                </button>
                <button
                  onClick={() => setShowEmailModal(true)}
                  className="px-3 py-1.5 text-sm bg-accent text-accent-fg rounded-lg hover:opacity-90 transition-colors flex items-center gap-1.5"
                  title="Send via Email"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  Email
                </button>
                <button
                  onClick={handleGeneratePdf}
                  disabled={generatingPdf}
                  className="px-3 py-1.5 text-sm bg-surface-sunken text-content rounded-lg hover:bg-border-token transition-colors disabled:opacity-50 flex items-center gap-1.5"
                  title="Regenerate PDF"
                >
                  {generatingPdf ? (
                    <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                  ) : (
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                  )}
                  Regenerate
                </button>
              </>
            ) : (
              <button
                onClick={handleGeneratePdf}
                disabled={generatingPdf}
                className="px-3 py-1.5 text-sm bg-accent text-accent-fg rounded-lg hover:opacity-90 transition-colors flex items-center gap-1.5 disabled:opacity-50"
                title="Generate PDF"
              >
                {generatingPdf ? (
                  <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                ) : (
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                )}
                Generate PDF
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Summary Cards.

          Max Demand / Reactive Energy / Power Factor are ELECTRICITY-ONLY and
          are not rendered for water. They are not merely mislabelled on a
          water bill — they are meaningless:
            · Max Demand and Reactive Energy are always exactly 0,00 (a water
              meter reports neither; bill-calculator gates both on `!isWater`,
              index.ts:1654-1669).
            · Power Factor was FABRICATED. cos(atan(0 / delta)) === 1 for any
              non-zero consumption, so every water bill displayed a confident
              "1,00" — a perfect power factor for a device with no power
              factor. A zero would have been merely wrong; a plausible-looking
              1,00 is worse, because nothing about it invites a second look.
          Hiding beats zeroing here: an absent card asks no question, whereas
          "0,00 kVA" reads as a measurement.

          Grid: electricity keeps `md:grid-cols-4` exactly as before (desktop
          at 1440px is unchanged, all four cards). Water renders one card and
          uses `md:grid-cols-2` so it sits at half width rather than stranded
          in a quarter-width cell with three empty ones beside it. */}
      <div className={`grid grid-cols-1 gap-6 mb-8 ${isWater ? 'md:grid-cols-2' : 'md:grid-cols-4'}`}>
        <Card className="shadow-sm p-6">
          <p className="text-sm text-content-muted">Consumption</p>
          {/* `delta_kwh` holds kL on a water bill — the column name is a
              historical artefact, not a unit. `consumption_unit` is the
              server's answer for this exact value. */}
          <p className="text-2xl font-bold text-content">{formatNumber(bill.delta_kwh)} {bill.consumption_unit}</p>
          <div className="mt-2 text-sm text-content-muted">
            {formatNumber(bill.opening_reading)} → {formatNumber(bill.closing_reading)}
          </div>
        </Card>

        {!isWater && (
          <Card className="shadow-sm p-6">
            <p className="text-sm text-content-muted">Max Demand</p>
            <p className="text-2xl font-bold text-content">{formatNumber(bill.max_kva)} kVA</p>
            {bill.max_kva_timestamp && (
              <div className="mt-2 text-sm text-content-muted">
                {formatDate(bill.max_kva_timestamp)}
                {bill.max_kva_tou_bucket && (
                  <div className="mt-1">{getTOUBadge(bill.max_kva_tou_bucket)}</div>
                )}
              </div>
            )}
          </Card>
        )}

        {!isWater && (
          <Card className="shadow-sm p-6">
            <p className="text-sm text-content-muted">Reactive Energy</p>
            <p className="text-2xl font-bold text-content">{formatNumber(bill.reactive_kvarh)} kVArh</p>
            {bill.delta_kwh > 0 && (
              <div className="mt-2 text-sm text-content-muted">
                {formatNumber((bill.reactive_kvarh / bill.delta_kwh) * 100, 1)}% of kWh
              </div>
            )}
          </Card>
        )}

        {!isWater && (
          <Card className="shadow-sm p-6">
            <p className="text-sm text-content-muted">Power Factor</p>
            <p className="text-2xl font-bold text-content">
              {bill.delta_kwh > 0 && bill.reactive_kvarh >= 0
                ? formatNumber(Math.cos(Math.atan(bill.reactive_kvarh / bill.delta_kwh)))
                : '-'}
            </p>
            <div className="mt-2 text-sm text-content-muted">
              Threshold: 0.96 (30% kVArh)
            </div>
          </Card>
        )}
      </div>

      {/* TOU Consumption */}
      {touConsumption.length > 0 && (
        <Card className="shadow-sm mb-8">
          <div className="px-6 py-4 border-b border-border-token">
            <h2 className="text-lg font-semibold text-content">Time-of-Use Breakdown</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-surface-sunken">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-content-muted uppercase tracking-wider">
                    Period
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-content-muted uppercase tracking-wider">
                    kWh
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-content-muted uppercase tracking-wider">
                    Rate
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-content-muted uppercase tracking-wider">
                    Amount
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border-token">
                {touConsumption.map((tou) => (
                  <tr key={tou.id}>
                    <td className="px-6 py-4">{getTOUBadge(tou.tou_bucket)}</td>
                    <td className="px-6 py-4 text-right font-mono">{formatNumber(tou.kwh)}</td>
                    <td className="px-6 py-4 text-right font-mono">
                      {formatRate(tou.rate_cents)}/kWh
                    </td>
                    <td className="px-6 py-4 text-right font-semibold">{formatCurrency(tou.amount_cents)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Audit Trail (frozen at bill creation for council-defensible billing) */}
      {(bill.register_totals || bill.interval_totals || bill.variance_flags) && (
        <BillAuditPanel bill={bill} />
      )}

      {/* Line Items */}
      <Card className="shadow-sm">
        <div className="px-6 py-4 border-b border-border-token">
          <h2 className="text-lg font-semibold text-content">Line Items</h2>
        </div>
        <DataTable
          data={sortedLineItems}
          columns={lineItemColumns}
          rowKey={(item) => item.id}
          emptyState={<p>No line items</p>}
        />
        {/* DataTable has no footer-row slot, so the Total row that used to be
            a <tfoot> is a summary strip below the table instead — same value. */}
        {lineItems.length > 0 && (
          <div className="px-6 py-3 border-t border-border-token bg-surface-sunken flex items-center justify-between">
            <span className="font-semibold text-content">Total</span>
            <span className="font-bold text-lg text-content">{formatCurrency(bill.total_amount_cents)}</span>
          </div>
        )}
      </Card>

      {/* Notes */}
      {bill.notes && (
        <Alert tone="warning" title="Notes" className="mt-8">
          {bill.notes}
        </Alert>
      )}

      {/* Success Message */}
      {emailSuccess && (
        <Alert tone="success" className="fixed bottom-4 right-4 shadow-lg">
          <div className="flex items-center gap-2">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            <p>{emailSuccess}</p>
            <button
              onClick={() => setEmailSuccess(null)}
              className="ml-2 hover:opacity-70"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </Alert>
      )}

      {/* Email Modal */}
      {showEmailModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-surface-raised rounded-xl shadow-2xl w-full max-w-md p-6">
            <h2 className="text-xl font-bold text-content mb-4">Send Bill via Email</h2>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-content mb-1">
                  Recipient Email
                </label>
                <input
                  type="email"
                  value={emailAddress}
                  onChange={(e) => setEmailAddress(e.target.value)}
                  placeholder="Leave empty to use customer email"
                  className="w-full px-4 py-2 border border-border-token rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
                <p className="mt-1 text-xs text-content-muted">
                  Leave empty to send to the customer's registered email address
                </p>
              </div>

              {error && <Alert tone="danger">{error}</Alert>}
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => {
                  setShowEmailModal(false);
                  setError(null);
                  setEmailAddress('');
                }}
                className="px-4 py-2 text-content bg-surface-sunken rounded-lg hover:bg-border-token transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSendEmail}
                disabled={sendingEmail}
                className="px-4 py-2 bg-accent text-accent-fg rounded-lg hover:opacity-90 transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {sendingEmail && (
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                )}
                Send Email
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
